import { Duration } from "luxon";

import { isNonProdStage } from "@/config";
import { createLogger } from "@/logger";

const log = createLogger("version");

const NEW_WORKER_TIMEOUT = Duration.fromObject({ seconds: 10 });
// update() can resolve before the found worker appears on registration.installing, so allow a
// short grace period for the updatefound event before concluding no update exists.
const UPDATE_FOUND_GRACE = Duration.fromObject({ seconds: 1 });
// Scope the Firebase SDK registers firebase-messaging-sw.js under. It owns the push subscription
// and serves none of the app bundle, so a stale-bundle recovery must leave it alone.
const FIREBASE_MESSAGING_SCOPE = "/firebase-cloud-messaging-push-scope";

/** Minimal structural view of a `ServiceWorker`, so tests can drive state transitions with fakes. */
export type ServiceWorkerLike = {
  state: ServiceWorkerState;
  postMessage: (message: unknown) => void;
  addEventListener: (type: "statechange", listener: () => void) => void;
  removeEventListener: (type: "statechange", listener: () => void) => void;
};

/** Minimal structural view of a `ServiceWorkerRegistration` covering the update lifecycle. */
export type ServiceWorkerRegistrationLike = {
  scope: string;
  installing: ServiceWorkerLike | null;
  waiting: ServiceWorkerLike | null;
  update: () => Promise<unknown>;
  unregister: () => Promise<boolean>;
  addEventListener: (type: "updatefound", listener: () => void) => void;
  removeEventListener: (type: "updatefound", listener: () => void) => void;
};

/**
 * Forces registered service workers to fetch, install, and activate the newest build.
 *
 * Recovery path for clients pinned to a stale bundle (WEB-2142): checks each registration for an
 * update, tells the staged worker to skip waiting, and waits for it to take over so the caller's
 * reload is served by the new worker. A failed update check falls through silently — callers
 * escalate to {@link unregisterServiceWorkers} when a reload doesn't unpin the bundle.
 */
export async function forceServiceWorkerUpdate(
  registrations?: readonly ServiceWorkerRegistrationLike[],
  timeoutMs: number = NEW_WORKER_TIMEOUT.toMillis(),
): Promise<void> {
  const targets = registrations ?? (await getRegistrations());
  await Promise.all(targets.map((registration) => applyUpdate(registration, timeoutMs)));
}

/**
 * Unregisters the app's service workers so the next page load fetches everything from the network.
 *
 * Last-resort escalation when {@link forceServiceWorkerUpdate} did not unpin a stale bundle. The
 * Firebase messaging worker is spared: unregistering it destroys the push subscription behind the
 * account's FCM token, which then fails every send forever (WEB-2214).
 */
export async function unregisterServiceWorkers(
  registrations?: readonly ServiceWorkerRegistrationLike[],
): Promise<void> {
  const candidates = registrations ?? (await getRegistrations());
  const targets = candidates.filter(servesAppBundle);

  if (isNonProdStage) {
    log.info("Unregistering app service workers", {
      unregistered: targets.map((registration) => registration.scope),
      preserved: candidates.filter((registration) => !servesAppBundle(registration)).map(({ scope }) => scope),
    });
  }

  await Promise.all(targets.map((registration) => registration.unregister().catch(() => false)));
}

function servesAppBundle(registration: ServiceWorkerRegistrationLike): boolean {
  return !registration.scope.replace(/\/$/, "").endsWith(FIREBASE_MESSAGING_SCOPE);
}

async function getRegistrations(): Promise<readonly ServiceWorkerRegistrationLike[]> {
  if (!("serviceWorker" in navigator)) return [];
  try {
    return await navigator.serviceWorker.getRegistrations();
  } catch {
    return [];
  }
}

async function applyUpdate(registration: ServiceWorkerRegistrationLike, timeoutMs: number): Promise<void> {
  const updateFound = waitForUpdateFound(registration, UPDATE_FOUND_GRACE.toMillis());
  try {
    await registration.update();
  } catch {
    // A transient failure here must not drop the registration: the caller reloads regardless and
    // escalates to unregisterServiceWorkers when the client is still stale afterwards.
    return;
  }

  const waiting = await waitForWaitingWorker(registration, updateFound, timeoutMs);
  if (!waiting) return;

  const activated = waitForState(waiting, (state) => state === "activated" || state === "redundant", timeoutMs);
  waiting.postMessage({ type: "SKIP_WAITING" });
  await activated;
}

/**
 * Resolves with the staged (installed-but-waiting) worker, or null when no update is pending.
 *
 * An update found by `registration.update()` may still be installing — or not even attached to
 * `registration.installing` yet — when the promise resolves, so this waits for `updatefound`
 * and then for the installing worker to leave the `installing` state before deciding.
 */
async function waitForWaitingWorker(
  registration: ServiceWorkerRegistrationLike,
  updateFound: Promise<boolean>,
  timeoutMs: number,
): Promise<ServiceWorkerLike | null> {
  if (registration.waiting) return registration.waiting;

  const installing = registration.installing ?? ((await updateFound) ? registration.installing : null);
  if (!installing) return null;

  const state = await waitForState(installing, (current) => current !== "installing", timeoutMs);
  return state === "installed" ? installing : null;
}

/** Resolves true when `updatefound` fires, or false after the grace period elapses. */
function waitForUpdateFound(registration: ServiceWorkerRegistrationLike, graceMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    const finish = (found: boolean) => {
      registration.removeEventListener("updatefound", onUpdateFound);
      clearTimeout(timer);
      resolve(found);
    };
    const onUpdateFound = () => finish(true);
    const timer = setTimeout(() => finish(false), graceMs);
    registration.addEventListener("updatefound", onUpdateFound);
  });
}

function waitForState(
  worker: ServiceWorkerLike,
  isDone: (state: ServiceWorkerState) => boolean,
  timeoutMs: number,
): Promise<ServiceWorkerState> {
  return new Promise((resolve) => {
    if (isDone(worker.state)) {
      resolve(worker.state);
      return;
    }
    const finish = () => {
      worker.removeEventListener("statechange", onStateChange);
      clearTimeout(timer);
      resolve(worker.state);
    };
    const onStateChange = () => {
      if (isDone(worker.state)) finish();
    };
    const timer = setTimeout(finish, timeoutMs);
    worker.addEventListener("statechange", onStateChange);
  });
}
